import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import isEqual from 'lodash/fp/isEqual';
import { startOfToday } from 'date-fns/startOfToday';
import { startOfMonth } from 'date-fns/startOfMonth';

import { DrawerV2, ButtonV2, DatePicker, Slider, dsV2 } from '@bloomays-lib/ui.shared';
import type { DatePickerValue } from '@bloomays-lib/ui.shared';
import type { IMissionCardDetails } from '@bloomays-lib/types.shared';
import { UPDATE_MISSION } from '@bloomays-lib/adapter.api-bloomer';
import { convertDateToStringFormat, isBefore, parseStringToDate } from '@bloomays-lib/utils.shared';

import { cancel, notify } from '../../../../helpers/toastify';
import { FieldV2, InputV2, SelectV2, Row2, InfoV2, DividerV2, CheckRow } from '../formFields';

export type EditMissionDrawerProps = {
  open: boolean;
  onClose: () => void;
  onCreateAmendment: () => void;
  mission: IMissionCardDetails;
  jobOwnerName?: string;
};

/**
 * Édition de la mission en panneau latéral (DrawerV2) — refonte V2 de `ModifyMission`.
 * Réutilise la mutation `UPDATE_MISSION` et la logique de validation d'origine (état modifié,
 * dates éditables uniquement avant démarrage). Le prix / la TVA / la prolongation passent par
 * un avenant (lien direct dans l'encart d'information).
 */
export const EditMissionDrawer = ({
  open,
  onClose,
  onCreateAmendment,
  mission,
  jobOwnerName,
}: EditMissionDrawerProps) => {
  const { t } = useTranslation(['mission', 'random', 'notify', 'missionDashboard']);

  const initialBegin = useMemo(() => parseStringToDate(mission.beginMission, 'yyyy-MM-dd'), [mission.beginMission]);
  const initialEnd = useMemo(() => parseStringToDate(mission.endMission, 'yyyy-MM-dd'), [mission.endMission]);

  const isMissionStarted = useMemo(() => {
    if (!initialBegin) return false;
    return !isBefore(startOfToday(), initialBegin);
  }, [initialBegin]);

  const [label, setLabel] = useState<string>(mission.label ?? '');
  const [beginMission, setBeginMission] = useState<DatePickerValue>(initialBegin);
  const [endMission, setEndMission] = useState<DatePickerValue>(initialEnd);
  const [certifyNoAmendment, setCertifyNoAmendment] = useState(false);
  const [contractType, setContractType] = useState<string>(mission.contractType || '');
  const [shouldBeCountAsDeal, setShouldBeCountAsDeal] = useState<boolean>(!!mission.shouldBeCountAsDeal);
  const [replacement, setReplacement] = useState<boolean>(!!mission.replacement);
  const [hoursPerDay, setHoursPerDay] = useState<number>(mission.hoursPerDays);
  const [enableCRAUpload, setEnableCRAUpload] = useState<boolean>(!!mission.enableCRAUpload);
  const [enableBusinessReferalNpsClient, setEnableBusinessReferalNpsClient] = useState<boolean>(
    !!mission.enableBusinessReferalNpsClient,
  );
  const [enableBusinessReferalNpsBloomer, setEnableBusinessReferalNpsBloomer] = useState<boolean>(
    !!mission.enableBusinessReferalNpsBloomer,
  );
  const [modified, setModified] = useState(false);

  const [updateMissionMutation, { loading }] = useMutation(UPDATE_MISSION);

  const initialState = useMemo(
    () => ({
      label: mission.label ?? '',
      contractType: mission.contractType || '',
      shouldBeCountAsDeal: !!mission.shouldBeCountAsDeal,
      replacement: !!mission.replacement,
      hoursPerDay: mission.hoursPerDays,
      enableCRAUpload: !!mission.enableCRAUpload,
      enableBusinessReferalNpsClient: !!mission.enableBusinessReferalNpsClient,
      enableBusinessReferalNpsBloomer: !!mission.enableBusinessReferalNpsBloomer,
      beginMission: initialBegin,
      endMission: initialEnd,
    }),
    [mission, initialBegin, initialEnd],
  );

  useEffect(() => {
    const currentState = {
      label,
      contractType,
      shouldBeCountAsDeal,
      replacement,
      hoursPerDay,
      enableCRAUpload,
      enableBusinessReferalNpsClient,
      enableBusinessReferalNpsBloomer,
      beginMission,
      endMission,
    };
    setModified(!isEqual(initialState, currentState));
  }, [
    label,
    contractType,
    shouldBeCountAsDeal,
    replacement,
    hoursPerDay,
    enableCRAUpload,
    enableBusinessReferalNpsClient,
    enableBusinessReferalNpsBloomer,
    beginMission,
    endMission,
    initialState,
  ]);

  const save = async () => {
    const trimmedLabel = label.trim();
    const originalLabel = (mission.label ?? '').trim();
    const labelChanged = trimmedLabel !== originalLabel;
    if (labelChanged && !trimmedLabel) {
      notify('error', t('actionModals.editMission.labelRequired', { ns: 'missionDashboard' }));
      return;
    }
    const payload: Record<string, unknown> = {
      label: trimmedLabel ? trimmedLabel : undefined,
      shouldBeCountAsDeal,
      replacement,
      contractType: contractType ? contractType : undefined,
      hoursPerDays: hoursPerDay,
      enableCRAUpload,
      enableBusinessReferalNpsClient,
      enableBusinessReferalNpsBloomer,
    };
    if (!isMissionStarted) {
      if (beginMission != null) payload.beginMission = convertDateToStringFormat(beginMission, 'yyyy-MM-dd');
      if (endMission != null) payload.endMission = convertDateToStringFormat(endMission, 'yyyy-MM-dd');
    }
    try {
      notify('info', t('updateInProgress', { ns: 'notify' }), null, { autoClose: 800, hideProgressBar: true });
      const response = await updateMissionMutation({
        variables: { input: { recordId: mission.recordId, ...payload } },
      });
      if (response?.data.updateMission) {
        notify('success', t('updateSuccess', { ns: 'notify' }));
        onClose();
      }
    } catch (e: any) {
      cancel();
      if (e.graphqlErrors?.[0]?.extensions?.code === 'BAD_USER_INPUT') {
        const invalidArgs = e.graphqlErrors?.[0]?.extensions?.invalidArgs as Array<{ missing: string }>;
        if (invalidArgs)
          notify('error', t('missingFieldError', { ns: 'notify', missingField: invalidArgs[0].missing as string }));
      } else {
        notify('error', t('randomUpdateError', { ns: 'notify' }));
      }
    }
  };

  return (
    <DrawerV2
      open={open}
      onClose={onClose}
      title={t('actions.modifyMission', { ns: 'missionDashboard' })}
      subtitle={mission.label}
      width={480}
      footer={
        <>
          <ButtonV2 variant="secondary" onClick={onClose}>
            {t('cancel', { ns: 'random' })}
          </ButtonV2>
          <ButtonV2
            onClick={save}
            disabled={!modified}
            loading={loading}
            startIcon={<SaveRoundedIcon sx={{ fontSize: 15 }} />}
          >
            {t('save', { ns: 'random' })}
          </ButtonV2>
        </>
      }
    >
      <InfoV2 tone="blue" icon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}>
        {t('actionModals.editMission.labelAmendmentWarning', { ns: 'missionDashboard' })}
      </InfoV2>

      <FieldV2
        label={t('actionModals.editMission.labelLabel', { ns: 'missionDashboard' })}
        hint={t('actionModals.editMission.labelHint', { ns: 'missionDashboard' })}
      >
        <InputV2 ariaLabel="editmission-label" value={label} onChange={setLabel} />
      </FieldV2>

      <InfoV2 tone="blue" icon={<LockRoundedIcon sx={{ fontSize: 16 }} />}>
        {t('actionModals.editMission.infoBefore', { ns: 'missionDashboard' })}{' '}
        <Box
          component="span"
          role="button"
          tabIndex={0}
          onClick={onCreateAmendment}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') onCreateAmendment();
          }}
          sx={{ fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '2px' }}
        >
          {t('actionModals.editMission.createAmendmentLink', { ns: 'missionDashboard' })}
        </Box>
      </InfoV2>

      <FieldV2
        label={t('actionModals.editMission.jobOwnerLabel', { ns: 'missionDashboard' })}
        hint={t('actionModals.editMission.jobOwnerHint', { ns: 'missionDashboard' })}
      >
        <InputV2 value={jobOwnerName ?? '—'} disabled readOnly />
      </FieldV2>

      {!mission.contractType ? (
        <FieldV2 label={t('contractTypeTitle')}>
          <SelectV2
            value={contractType}
            onChange={setContractType}
            placeholder={t('drawers.common.choose', { ns: 'missionDashboard' })}
            options={[
              {
                value: 'devis',
                label: t('actionModals.editMission.contractOptions.devis', { ns: 'missionDashboard' }),
              },
              {
                value: 'clientContract',
                label: t('actionModals.editMission.contractOptions.clientContract', { ns: 'missionDashboard' }),
              },
              {
                value: 'bloomaysContract',
                label: t('actionModals.editMission.contractOptions.bloomaysContract', { ns: 'missionDashboard' }),
              },
              { value: 'BDC', label: t('actionModals.editMission.contractOptions.BDC', { ns: 'missionDashboard' }) },
            ]}
          />
        </FieldV2>
      ) : null}

      <DividerV2 />

      {!isMissionStarted ? (
        <CheckRow checked={certifyNoAmendment} onChange={setCertifyNoAmendment}>
          {t('certifyNoAmendment')}
        </CheckRow>
      ) : null}
      <Row2>
        <FieldV2
          label={t('dateDebut')}
          hint={
            isMissionStarted ? t('actionModals.editMission.beginStartedHint', { ns: 'missionDashboard' }) : undefined
          }
        >
          <DatePicker
            ariaLabel="editmission-begindate"
            disabled={isMissionStarted || !certifyNoAmendment}
            value={beginMission}
            minDate={startOfMonth(new Date())}
            maxDate={endMission ?? undefined}
            handleChange={setBeginMission}
          />
        </FieldV2>
        <FieldV2
          label={t('dateFin')}
          hint={isMissionStarted ? t('actionModals.editMission.endStartedHint', { ns: 'missionDashboard' }) : undefined}
        >
          <DatePicker
            ariaLabel="editmission-enddate"
            disabled={isMissionStarted || !certifyNoAmendment}
            value={endMission}
            minDate={beginMission ?? undefined}
            handleChange={setEndMission}
          />
        </FieldV2>
      </Row2>

      <DividerV2 />

      <CheckRow checked={replacement} onChange={setReplacement}>
        {t('isReplacement')}
      </CheckRow>
      <CheckRow checked={shouldBeCountAsDeal} onChange={setShouldBeCountAsDeal}>
        {t('shouldBeCountAsDeal')}
      </CheckRow>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '4px 0 12px' }}>
        <Box sx={{ fontSize: '12.5px', color: dsV2.color.t2, flex: 1 }}>
          {t('actionModals.editMission.dayDuration', { ns: 'missionDashboard' })}
        </Box>
        <Slider
          sx={{ width: 140 }}
          label={t('actionModals.editMission.dayDuration', { ns: 'missionDashboard' })}
          min={0}
          max={12}
          step={0.5}
          marks={[]}
          value={hoursPerDay}
          defaultValue={hoursPerDay}
          onChange={(_e, value) => setHoursPerDay(Array.isArray(value) ? value[0] : value)}
        />
        <Box
          sx={{
            fontFamily: dsV2.font.sans,
            fontVariantNumeric: 'tabular-nums',
            fontSize: 13,
            fontWeight: 500,
            color: dsV2.color.bleu,
            width: 36,
            textAlign: 'right',
          }}
        >
          {hoursPerDay}h
        </Box>
      </Box>
      <Box sx={{ fontSize: '10.5px', color: dsV2.color.t4, marginBottom: '12px' }}>
        <span role="img" aria-label="attention">
          ⚠️
        </span>{' '}
        {t('actionModals.editMission.notRetroactiveNote', { ns: 'missionDashboard' })}
      </Box>

      <DividerV2 />

      <CheckRow checked={enableCRAUpload} onChange={setEnableCRAUpload}>
        {t('canUploadCra')}
      </CheckRow>
      <CheckRow checked={enableBusinessReferalNpsClient} onChange={setEnableBusinessReferalNpsClient}>
        {t('businessReferalNpsClient')}
      </CheckRow>
      <CheckRow checked={enableBusinessReferalNpsBloomer} onChange={setEnableBusinessReferalNpsBloomer}>
        {t('businessReferalNpsBloomer')}
      </CheckRow>
    </DrawerV2>
  );
};

export default EditMissionDrawer;
